import { createTheme, ThemeProvider as MuiThemeProvider } from '@mui/material/styles'

interface Props {
  children: React.ReactNode
}

/**
 * 新UI用のThemeProvider
 * MUIのデフォルトスタイルのみを使用し、既存のカスタム設定を完全に無視する
 *
 * 用途:
 * - src/shared/ui/ 配下の新しいコンポーネントで使用（Figma MCP で実装）
 * - 既存の component overrides, palette, typography の影響を受けたくない場合
 *
 * 含まれるもの:
 * - MUI デフォルトのみ（primary = 青、フォント = Roboto など）
 *
 * 含まれないもの:
 * - palette (既存の緑色 PRIMARY を無視)
 * - typography (既存の Public Sans を無視)
 * - shadows / customShadows (既存のカスタム shadow を無視)
 * - componentsOverrides (MUIコンポーネントのカスタムスタイル)
 * - shape.borderRadius (既存の 8px を無視、デフォルト 4px)
 * - presets / contrast / RTL
 * - CssBaseline（`box-sizing: border-box` を含む）。アプリは `src/app/app.tsx` で
 *   この Provider の内側に `CssBaseline` を描画する。Storybook は `.storybook/preview.tsx` で
 *   `box-sizing` の規則だけを同じ形で当てている（#2455）。この Provider だけで包んだ描画は
 *   `content-box` のままなので、`width` と `border` を同じ要素に持つ部品は実機より大きくなる
 *
 * 注意:
 * 全コンポーネント実装後、共通化可能な色・フォントをパレット化する際に
 * このファイルを更新する。
 */
export function CleanThemeProvider ({ children }: Props): JSX.Element {
  // 既存の設定を完全に無視し、MUI デフォルト + 新UI 共通フォント(Noto Sans JP)を使用
  const theme = createTheme({
    typography: {
      fontFamily: '"Noto Sans JP", sans-serif'
    }
  })

  return (
    <MuiThemeProvider theme={theme}>
      {children}
    </MuiThemeProvider>
  )
}
